
// Build-time version info that gets updated with each deployment
export const APP_VERSION = {
  version: import.meta.env.VITE_VERSION || '1.0.0',
  buildTimestamp: import.meta.env.VITE_BUILD_TIMESTAMP || Date.now(),
  buildDate: import.meta.env.VITE_BUILD_DATE || new Date().toISOString(),
  commit: import.meta.env.VITE_COMMIT_HASH || 'latest',
  deploymentId: import.meta.env.VITE_DEPLOYMENT_ID || `dep-${Date.now()}`,
  environment: import.meta.env.MODE || 'development'
};

export const getVersionInfo = () => {
  const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : 'server';
  const isMobile = typeof navigator !== 'undefined' 
    ? /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
    : false;
  
  const viewport = typeof window !== 'undefined' 
    ? { width: window.innerWidth, height: window.innerHeight }
    : { width: 0, height: 0 };

  return {
    ...APP_VERSION,
    userAgent,
    isMobile,
    viewport,
    cacheKey: `${APP_VERSION.deploymentId}-${APP_VERSION.buildTimestamp}`,
    lastUpdated: new Date().toISOString()
  };
};

// Force cache invalidation with enhanced clearing
export const invalidateCache = async () => {
  console.log('🧹 Starting cache invalidation...');
  
  if ('serviceWorker' in navigator) {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      for (let registration of registrations) {
        await registration.unregister();
        console.log('Service Worker unregistered:', registration.scope);
      }
    } catch (error) {
      console.warn('Failed to unregister service workers:', error);
    }
  }

  // Clear all caches with enhanced logging
  if ('caches' in window) {
    try {
      const cacheNames = await caches.keys();
      console.log('Found caches to clear:', cacheNames);
      await Promise.all(
        cacheNames.map(cacheName => {
          console.log(`Clearing cache: ${cacheName}`);
          return caches.delete(cacheName);
        })
      );
      console.log('✅ All caches cleared');
    } catch (error) {
      console.warn('Failed to clear caches:', error);
    }
  }

  // Clear storage with enhanced logging
  try {
    const localStorageKeys = Object.keys(localStorage);
    const sessionStorageKeys = Object.keys(sessionStorage);
    
    console.log('Clearing localStorage keys:', localStorageKeys);
    console.log('Clearing sessionStorage keys:', sessionStorageKeys);
    
    localStorage.clear();
    sessionStorage.clear();
    console.log('✅ Storage cleared');
  } catch (error) {
    console.warn('Failed to clear storage:', error);
  }
};

// Enhanced version checking with better error handling
export const checkForUpdates = async () => {
  try {
    const cacheBuster = Date.now();
    const response = await fetch(`/version.json?cb=${cacheBuster}`, {
      cache: 'no-cache',
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      }
    });
    
    if (response.ok) {
      const serverVersion = await response.json();
      const currentVersion = getVersionInfo();
      
      console.log('Version check:', {
        server: serverVersion,
        current: currentVersion,
        hasUpdate: serverVersion.deploymentId !== currentVersion.deploymentId
      });
      
      if (serverVersion.deploymentId !== currentVersion.deploymentId) {
        console.log('🆕 New version available:', serverVersion);
        return { hasUpdate: true, newVersion: serverVersion };
      }
    } else {
      console.warn('Failed to fetch version info:', response.status, response.statusText);
    }
    
    return { hasUpdate: false };
  } catch (error) {
    console.warn('Failed to check for updates:', error);
    return { hasUpdate: false };
  }
};
